import type { WorkspaceOut } from "@sagot/api-client";
import { AUDIENCE_CODES } from "@sagot/constants";
import { useMemo } from "react";
import { useAgentsList } from "../../features/deploy-knowledge/shared/use-shared-queries";
import { readOnboarding } from "../use-onboarding";

/** Same default as the Knowledge Base "Add source" dialog: visible to everyone until restricted there. */
const NEW_SOURCE_AUDIENCES: readonly number[] = [AUDIENCE_CODES.public];

export interface SourceTargets {
  audiences: readonly number[];
  /**
   * The onboarding agent once step 3 created it. Before that there is no agent: the API links the
   * workspace's first agent to every source when step 3 applies, so nothing is sent here.
   * The server re-checks the agent exists and that the caller may edit it.
   */
  agentIds: readonly string[];
  /** False while the agent lookup is still pending, so a source is never created unlinked by mistake. */
  ready: boolean;
}

export function useSourceTargets(workspace: WorkspaceOut | undefined): SourceTargets {
  const savedAgentId = readOnboarding(workspace).data.agent_id;
  const agents = useAgentsList();
  const stillThere = agents.data?.some((agent) => agent.id === savedAgentId) === true;
  const linkTo = savedAgentId && (stillThere || agents.isError) ? savedAgentId : null;
  const lookingUp = Boolean(savedAgentId) && agents.isPending;
  return useMemo(() => ({ audiences: NEW_SOURCE_AUDIENCES, agentIds: linkTo ? [linkTo] : [], ready: !lookingUp }), [linkTo, lookingUp]);
}
